import { useContext, useEffect } from "react";
import { AppContext } from "~/contexts/AppContext";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVenusMars } from "@fortawesome/free-solid-svg-icons/faVenusMars";
import { faMars } from "@fortawesome/free-solid-svg-icons/faMars";
import { faMedal } from "@fortawesome/free-solid-svg-icons/faMedal";
import { faVenus } from "@fortawesome/free-solid-svg-icons/faVenus";
import { faThumbsUp } from "@fortawesome/free-solid-svg-icons/faThumbsUp";
import { faThumbsDown } from "@fortawesome/free-solid-svg-icons/faThumbsDown";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons/faCircleQuestion";

import { useTranslation } from "react-i18next";
import { capitalizeFirst, fixBold } from "~/util/util";
import { GroupResult } from "~/types";
import styled from "styled-components";
import TypingTextV2 from "~/components/TypingTextV2";

const GenderButton = styled.button`
    background-color: #F0F1F5;
    border: none;
    border-radius: 12px;
    padding: 12px 20px;
    color: #202020;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    justify-content: center;
    min-width: 0;
    white-space: nowrap;
    
    @media only screen and (max-width: 450px) {
        padding: 10px 12px;
        font-size: 14px;
        gap: 6px;
        border-radius: 10px;
    }
    
    @media only screen and (max-width: 350px) {
        padding: 8px 10px;
        font-size: 12px;
        gap: 4px;
    }
`;

const GenderButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    margin-top: 15px;
    width: 100%;
    
    @media only screen and (max-width: 450px) {
        gap: 8px;
    }
    
    @media only screen and (max-width: 350px) {
        gap: 6px;
    }
`;

const PrivacyText = styled.p`
    font-size: 12px !important;
    color: #ccc !important;
    margin: 8px 0 0 0 !important;
    
    @media only screen and (max-width: 450px) {
        font-size: 11px !important;
    }
    
    @media only screen and (max-width: 350px) {
        font-size: 10px !important;
    }
`;  

interface GenderResultSlideProps {
    groupResult?: GroupResult;
    ratingValue: number;
    submitGender: React.ChangeEventHandler;
    fadeInComplete: boolean;
    onAnimationComplete: () => void;
}

const GenderResultSlide: React.FC<GenderResultSlideProps> = props => {
    const { profile } = useContext(AppContext)!;
    const { t } = useTranslation();

    if(!profile) return null;

    const handleGenderSelect = (gender: string) => {
        const event = {
            target: { value: gender }
        } as React.ChangeEvent<HTMLSelectElement>;
        props.submitGender(event);
    };

    useEffect(() => {
        if (!profile.gender && props.fadeInComplete) {
            props.onAnimationComplete();
        }
    }, [props.fadeInComplete, profile.gender]);

    if (!profile.gender || !props.groupResult)
        return (
            <>
                <span className='header-icon-row'>
                    <span className='icon'><FontAwesomeIcon icon={faVenusMars} /></span>
                    <span className='icon'><FontAwesomeIcon icon={faCircleQuestion} /></span>
                </span>
                <p style={{ fontSize: '16px', marginBottom: '10px', marginTop: '0', fontWeight: 'bold' }}>{t('results_gender_input_let_me_show_you')}</p>
                <GenderButtonContainer>
                    <GenderButton onClick={() => handleGenderSelect('male')}>
                        <span>👱‍♂️</span>
                        {capitalizeFirst(t('general_male'))}
                    </GenderButton>
                    <GenderButton onClick={() => handleGenderSelect('female')}>
                        <span>👱‍♀️</span>
                        {capitalizeFirst(t('general_female'))}
                    </GenderButton>
                </GenderButtonContainer>
                <PrivacyText>{t('results_gender_input_disclaimer')}</PrivacyText>
            </>
        );

    if (profile.gender === 'other')
        return null;

    if (props.groupResult.firstRating) {
        const genderIcon = profile.gender === 'male' ? faMars : faVenus;
        return (
            <>
                <span className='header-icon-row'>
                    <span className='icon'><FontAwesomeIcon icon={genderIcon} /></span>
                    <span className='icon'><FontAwesomeIcon icon={faMedal} /></span>
                </span>
                <TypingTextV2 active={props.fadeInComplete} delay={10} onComplete={props.onAnimationComplete}>
                    <p>
                        {profile.gender === 'male'
                            ? fixBold(t('results_gender_first_rating_male'))
                            : fixBold(t('results_gender_first_rating_female'))
                        }
                    </p>
                </TypingTextV2>
            </>
        );
    }

    const genderIcon = profile.gender === 'male' ? faMars : faVenus;
    const genderStr = profile.gender === 'male' ? t('general_male_plural') : t('general_female_plural');

    const userRating = props.ratingValue;
    const ratingsDiff = Math.abs(userRating - (props.groupResult.rating ?? 50));

    let agreementMessage;
    let agreementIcon;

    if(ratingsDiff <= 15) {
        agreementIcon = faThumbsUp;
        agreementMessage = fixBold(t('results_gender_strong_agreement', { genderStr }));
    } else if (ratingsDiff <= 30) {
        agreementIcon = faThumbsUp;
        agreementMessage = fixBold(t('results_gender_weak_agreement', { genderStr }));
    } else if(ratingsDiff <= 50) {
        agreementIcon = faThumbsDown;
        agreementMessage = fixBold(t('results_gender_weak_disagreement', { genderStr }));
    } else {
        agreementIcon = faThumbsDown;
        agreementMessage = fixBold(t('results_gender_strong_disagreement', { genderStr }));
    }

    return (
        <>
            <span className='header-icon-row'>
                <span className='icon'><FontAwesomeIcon icon={genderIcon} /></span>
                <span className='icon'><FontAwesomeIcon icon={agreementIcon} /></span>
            </span>
            <TypingTextV2 active={props.fadeInComplete} delay={10} onComplete={props.onAnimationComplete}>
                <p>{agreementMessage}</p>
            </TypingTextV2>
        </>
    );
};

export default GenderResultSlide;